import React, { useMemo, useState } from 'react'
import './setNewPassword.scss';

import { useNavigate } from 'react-router-dom';
import { updatePassword } from '../../common/api/apiCalls';
import { useTranslation } from 'react-i18next';
import { setPasswordSchema } from '../../common/consts/yupSchems';
import { ValidationError } from 'yup';
import { svgs } from '../../common/assats';
import PasswordInputWithIcon from '../../components/PasswordInputWithIcon';
import Button from '../../components/Button';
import { useAlert } from '../../common/contexts/AlertContext';

const SetNewPassword = () => {
    const { t } = useTranslation()
    const [password, setPassword] = useState("")
    const [secondPassword, setSecondPassword] = useState("")
    const [checkbox, setCheckbox] = useState(false)
    const [checkboxBorder, setCheckboxBorder] = useState(false)
    const [errors, setErrors] = useState<{ password?: string[] }>({})
    const passwordsEquals: boolean | undefined = useMemo(() => Boolean(password && secondPassword) ? Boolean(password === secondPassword) : undefined, [password, secondPassword])
    const navigate = useNavigate();
    const getText = (text: string) => t(`set_password.${text}`)
    const alert = useAlert()
    async function validate() {
        try {
            await setPasswordSchema.validate({ password }, { abortEarly: false });
            setErrors({});
            return true; // Validation succeeded
        } catch (err: any) {
            if (err instanceof ValidationError) {
                const validationErrors: { email?: string[]; password?: string[] } = { email: [], password: [] };
                err.inner.forEach((error: any) => {
                    const path = error.path as keyof typeof validationErrors;
                    if (path && validationErrors[path]) {
                        validationErrors[path]?.push(error.message); // Collect multiple errors for each field
                    }
                });
                setErrors(validationErrors);
            }
            return false; // Validation failed
        }
    };

    async function submit() {
        if (!password || !checkbox) {
            setCheckboxBorder(true);
            return;
        }
        if (await validate()) {
            if (password !== secondPassword) return;
            const res = await updatePassword({ password });
            if (res.success) {
                alert(getText("passwordHasSet"), "msg");
                navigate("/Home/Patients");
            } else {
                alert("Failed to update password", "error");
            }
        }
    }


    async function validatePassword(inputPassword: string) {
        try {
            await setPasswordSchema.validate({ password: inputPassword }, { abortEarly: false });
            setErrors((prev) => ({ ...prev, password: undefined }));
        } catch (err: any) {
            if (err instanceof ValidationError) {
                const validationErrors: { password?: string[] } = {};
                err.inner.forEach((error: any) => {
                    if (error.path === "password") {
                        validationErrors.password = validationErrors.password || [];
                        validationErrors.password.push(error.message);
                    }
                });
                setErrors(validationErrors);
            }
        }
    }

    const handlePasswordChange = (newPassword: string) => {
        setPassword(newPassword);
        validatePassword(newPassword);
    };

    const handleSecondPasswordChange = (newPassword: string) => {
        setSecondPassword(newPassword);
        validatePassword(newPassword);
    };


    return (
        <div className='set-new-password-main-container flex-center'>
            <div className='login-content'>
                <h3 className='headline'> {getText("welcome_to")}</h3>
                <svgs.logos.Logo className='logo' />
                <div className="input-wrapper">
                    <p className='text'>{getText("Enter_the_new_password_below")}</p>
                    <PasswordInputWithIcon
                        label={getText("New_password")}
                        errors={errors}
                        placeholder={getText('New_password')}
                        setValue={handlePasswordChange}
                        value={password}
                        filedCorrect={passwordsEquals}
                        showForgotPassword={false}
                    />
                    <PasswordInputWithIcon
                        label={getText("Confirm_New_Password")}
                        errors={errors}
                        placeholder={getText('Confirm_New_Password')}
                        setValue={handleSecondPasswordChange}
                        value={secondPassword}
                        filedCorrect={passwordsEquals}
                        showForgotPassword={false}

                    />
                    {errors.password ?
                        password === secondPassword ?
                            <p className='password-instructions-error'>{getText("The_password_doesn’t_meet_the_minimum_security_requirements")}</p>
                            :
                            <p className='password-instructions-error'>{getText("password_not_match")}</p>

                        :
                        <p className='password-instructions-text'>{getText("New_password_must_contain")}</p>
                    }
                    <div className="password-instructions">
                        <ul>
                            <li className={errors?.password?.includes("length") ? "red" : ""}>{getText("characters")}</li>
                            <li className={errors?.password?.includes("specialCharPattern") ? "red" : ""}>{getText("one_special_character")}</li>
                            <li className={errors?.password?.includes("numberPattern") ? "red" : ""}>{getText("one_number")}</li>
                        </ul>
                        <ul>

                            <li className={errors?.password?.includes("capitalLetterPattern") ? "red" : ""}>{getText("one_uppercase_character")}</li>
                            <li className={errors?.password?.includes("lowerLetterPattern") ? "red" : ""}>{getText("one_lowercase_character")}</li>
                        </ul>
                    </div>
                    <div className="terms-and-conditions-container">
                        <input onClick={() => setCheckbox(!checkbox)} className={`checkbox ${checkboxBorder && !checkbox ? "borderRed" : ""}`} type='checkbox' />
                        <span className='tac-text'>{getText("I_agree_to_the")}
                            <a href='/terms-and-conditions' target="_blank" rel="noopener noreferrer" className='link'>
                                {getText("Terms_and_Conditions")}
                            </a>
                        </span>
                    </div>
                </div>
                <Button
                    onClick={submit}
                    text={getText("Login")}
                    disabled={
                        !checkbox ||
                        !password ||
                        !secondPassword ||
                        password !== secondPassword ||
                        !!errors.password?.length
                    }
                />

            </div>
        </div>
    );
}

export default SetNewPassword
